import type { CommunityComment } from "../types";
import { useCommentStore } from "../store/comment-store";
import { usePostStore } from "../store/post-store";
import {
  fetchComments as apiFetchComments,
  fetchReplies as apiFetchReplies,
  createComment as apiCreateComment,
  deleteComment as apiDeleteComment,
  toggleCommentLike as apiToggleCommentLike,
} from "../community-api";
import { dedupFetch, invalidateCache } from "./request-dedup";

const COMMENT_SWR = { staleTime: 30_000 }; // 30 seconds

export async function loadComments(
  postId: string,
  page = 1,
  force = false,
): Promise<void> {
  const { data } = await dedupFetch(
    `comments:${postId}:p${page}`,
    () => apiFetchComments(postId, page),
    {
      ...COMMENT_SWR,
      force,
      onStaleRevalidate: (fresh) => {
        if (fresh) {
          if (page === 1) {
            useCommentStore
              .getState()
              .setComments(postId, fresh.comments, fresh.total, fresh.hasMore);
          } else {
            useCommentStore.getState().appendComments(postId, fresh.comments);
          }
        }
      },
    },
  );

  if (data) {
    if (page === 1) {
      useCommentStore
        .getState()
        .setComments(postId, data.comments, data.total, data.hasMore);
    } else {
      useCommentStore.getState().appendComments(postId, data.comments);
    }
  }
}

export async function addComment(
  postId: string,
  content: string,
  parentId?: string,
): Promise<CommunityComment> {
  const comment = await apiCreateComment(postId, content, parentId);
  useCommentStore.getState().addComment(postId, comment, parentId);
  // Update post comment count
  const post = usePostStore.getState().entities[postId];
  if (post) {
    usePostStore.getState().updatePost(postId, {
      stats: { ...post.stats, comments: post.stats.comments + 1 },
    });
  }
  invalidateCache(`comments:${postId}:p1`);
  return comment;
}

/**
 * 展开某个顶级评论的全部子评论（超过2条时调用）
 * 加载完成后直接更新 store 中该评论的 replies
 */
export async function loadReplies(
  postId: string,
  parentId: string,
): Promise<void> {
  // 从 store 取父评论的作者名，用于子评论"回复 xxx"显示
  const parentComment = useCommentStore.getState().entities[parentId];
  const parentAuthorName = parentComment?.author.displayName ?? "";
  const { replies } = await apiFetchReplies(parentId, postId, parentAuthorName);
  useCommentStore.getState().setReplies(postId, parentId, replies);
}

export async function removeComment(
  postId: string,
  commentId: string,
): Promise<void> {
  await apiDeleteComment(commentId);
  useCommentStore.getState().removeComment(postId, commentId);
  const post = usePostStore.getState().entities[postId];
  if (post) {
    usePostStore.getState().updatePost(postId, {
      stats: { ...post.stats, comments: Math.max(0, post.stats.comments - 1) },
    });
  }
}

export async function toggleCommentLike(
  commentId: string,
  isCurrentlyLiked: boolean,
): Promise<void> {
  // Optimistic
  useCommentStore.getState().updateComment(commentId, {
    isLiked: !isCurrentlyLiked,
    likes:
      (useCommentStore.getState().entities[commentId]?.likes ?? 0) +
      (isCurrentlyLiked ? -1 : 1),
  });

  try {
    await apiToggleCommentLike(commentId, isCurrentlyLiked);
  } catch {
    // Rollback
    const current = useCommentStore.getState().entities[commentId];
    useCommentStore.getState().updateComment(commentId, {
      isLiked: isCurrentlyLiked,
      likes: (current?.likes ?? 0) + (isCurrentlyLiked ? 1 : -1),
    });
  }
}
